import {publishEvent} from '@udemy-v2/event-tracking';
import {TrackableBuyable} from '@udemy-v2/browse-course';

type CouponApplicationMethod = 'manual_entry' | 'url_param';

/**
 * Base class for coupon-related tracking events
 */
abstract class CouponEvent {
    abstract readonly _type: string;

    constructor(
        public readonly buyables: TrackableBuyable[],
        public readonly couponCode: string,
    ) {}
}

/**
 * Fired whenever a coupon is applied on the CLP or cart page, either by manual
 * entry from the user or being included as a URL param
 */
export class CouponApplyEvent extends CouponEvent {
    readonly _type = 'CouponApplyEvent';

    constructor(
        buyables: TrackableBuyable[],
        couponCode: string,
        public readonly applicationMethod: CouponApplicationMethod,
    ) {
        super(buyables, couponCode);
    }
}

export class CouponApplied extends CouponEvent {
    readonly _type = 'CouponApplied';

    constructor(
        buyables: TrackableBuyable[],
        couponCode: string,
        public readonly applicationMethod: CouponApplicationMethod,
    ) {
        super(buyables, couponCode);
    }
}

/**
 * Publishes coupon apply events for tracking
 * Used when pricing API validates a coupon code
 *
 * @param code - The coupon code that was applied
 * @param applicationMethod - How the code was applied ('manual_entry' or 'url_param')
 * @param buyables - Array of buyable items the coupon applies to
 */
export function publishCouponApplyEvent(
    code: string,
    applicationMethod: CouponApplicationMethod,
    buyables: TrackableBuyable[],
): void {
    publishEvent(new CouponApplied(buyables, code, applicationMethod));
    publishEvent(new CouponApplyEvent(buyables, code, applicationMethod));
}
